import React from "react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import { BadgeCheck, CheckCircle2, AlertTriangle, ArrowRight, Info, Scale } from "lucide-react";

const doesCover = [
  "Eligible document packs supplied by Apex Quality Consultancy",
  "Written deliverables prepared by Apex Quality Consultancy (policies, procedures, forms, templates)",
  "Correction and resolution where major compliance issues arise directly due to documentation we supplied",
];

const doesNotCover = [
  "External Approved Quality Auditor fees",
  "Government fees, ASIC fees, insurance, worker screening costs",
  "Third party fees and surcharges",
  "Urgent work surcharges",
  "Completed consultation hours",
  "Implementation work already delivered",
  "NDIS registration, audit approval or audit outcomes",
  "Participant referrals or client numbers",
  "Audit outcomes that depend on provider implementation, staff qualifications, evidence, business operations or auditor assessment",
];

export default function QualityCommitment() {
  return (
    <main>
      <PageHero
        eyebrow="Our Promise"
        title="Documentation Quality Commitment"
        subtitle="Apex Quality Consultancy stands behind the quality of its documentation. If any major compliance issues arise directly due to documentation, policies, forms or templates supplied by us, we will work with you to correct and resolve the issue at no additional cost."
        breadcrumb={[{ label: "Quality Commitment" }]}
      />

      <section className="py-20 md:py-24">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <div className="flex items-start gap-4 mb-8">
              <div className="w-16 h-16 rounded-2xl bg-teal-soft text-teal flex items-center justify-center shrink-0">
                <BadgeCheck size={30} />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-navy leading-tight">We stand behind the quality of our work</h2>
                <span className="teal-divider mt-3" />
              </div>
            </div>

            <div className="space-y-4 text-slate-700 text-[16px] leading-relaxed">
              <p>
                Apex Quality Consultancy is committed to providing professional, evidence focused NDIS compliance
                documentation. Our policies, procedures, forms and templates are developed to align with the NDIS
                Practice Standards and common audit evidence expectations.
              </p>
              <p>
                If you experience any major compliance issue that arises directly due to documentation, policies,
                forms or templates supplied by Apex Quality Consultancy, please contact us. We will work with you to
                review the concern, correct the documentation and resolve the issue at no additional cost.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center gap-2.5 mb-4">
                  <CheckCircle2 size={20} className="text-emerald-600" />
                  <h3 className="text-navy font-bold text-lg">What is covered</h3>
                </div>
                <ul className="space-y-2.5">
                  {doesCover.map((c) => (
                    <li key={c} className="flex items-start gap-2.5 text-sm text-slate-700">
                      <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center gap-2.5 mb-4">
                  <AlertTriangle size={20} className="text-amber-600" />
                  <h3 className="text-navy font-bold text-lg">What is not covered</h3>
                </div>
                <ul className="space-y-2.5">
                  {doesNotCover.map((c) => (
                    <li key={c} className="flex items-start gap-2.5 text-sm text-slate-700">
                      <AlertTriangle size={16} className="text-amber-500 mt-0.5 shrink-0" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-10 p-6 rounded-2xl bg-mist border-l-4 border-navy">
              <div className="flex items-start gap-3">
                <Info size={20} className="text-navy shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-navy font-bold text-sm">Important note</h4>
                  <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                    Apex Quality Consultancy does not guarantee NDIS registration or audit outcomes. Audit and
                    registration outcomes depend on the provider’s implementation, staff qualifications, evidence,
                    business operations, participant records, application accuracy and auditor assessment.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 p-5 rounded-2xl bg-teal-soft border-l-4 border-teal flex items-start gap-3">
              <Scale size={20} className="text-teal shrink-0 mt-0.5" />
              <p className="text-sm text-slate-700 leading-relaxed">
                <strong className="text-navy">Australian Consumer Law.</strong> Nothing in this commitment limits
                rights available to you under Australian Consumer Law.
              </p>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-28 space-y-5">
              <div className="bg-navy text-white rounded-2xl p-7">
                <BadgeCheck className="text-teal-mid" size={28} />
                <h4 className="mt-4 font-bold text-lg">How to raise a concern</h4>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                  If you have a concern about documentation we have supplied, please contact us. We will review your
                  concern and work with you to correct and resolve it.
                </p>
                <Link to="/contact" className="mt-5 btn-primary w-full inline-flex items-center justify-center px-4 py-3 rounded-lg text-sm font-semibold gap-2">
                  Contact Us <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
